import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { useCountry } from "../contexts/CountryContext";
import { getCountryConfig } from "../lib/country";
import { MapPin, Star, Clock, User, CheckCircle, ArrowRight } from "lucide-react";
import { ProviderBadges } from "../components/shared/ProviderBadges";

const ProvidersPage = () => {
  const { countryCode } = useCountry();
  const showActivity = getCountryConfig(countryCode).showMarketplaceActivity !== false;
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('');
  const [debouncedCity, setDebouncedCity] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  // Debounce city input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCity(city), 400);
    return () => clearTimeout(timer);
  }, [city]);

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCity, verifiedOnly, countryCode]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      // Send country_code so the backend scopes results to the active market
      // (preview: uses UI toggle; production: matches Host header anyway).
      let url = `${API_URL}/providers?country_code=${encodeURIComponent(countryCode)}&`;
      if (debouncedCity) url += `city=${encodeURIComponent(debouncedCity)}&`;
      if (verifiedOnly) url += `verified_only=true&`;
      
      const response = await fetch(url);
      const data = await response.json();
      setProviders(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page providers-page">
      <div className="page-header">
        <h1>Find Professionals</h1>
        <p>{showActivity ? 'Browse local provider profiles, ratings and reviews.' : 'Browse local provider profiles.'} The ID Verified badge is shown only when a provider completes identity verification.</p>
      </div>

      <div className="filter-bar">
        <div className="filter-input-wrapper">
          <MapPin size={16} className="filter-input-icon" />
          <input 
            type="text"
            value={city} 
            onChange={(e) => setCity(e.target.value)}
            className="filter-input"
            placeholder="Enter your city or suburb"
            data-testid="city-filter"
          />
          {city && <button className="filter-clear" onClick={() => setCity('')} data-testid="city-clear">&times;</button>}
        </div>

        <label className="filter-checkbox">
          <input 
            type="checkbox" 
            checked={verifiedOnly} 
            onChange={(e) => setVerifiedOnly(e.target.checked)}
            data-testid="verified-filter"
          />
          <span>Verified Only</span>
        </label>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : providers.length > 0 ? (
        <div className="providers-grid" data-testid="providers-grid">
          {providers.map(provider => (
            <Link 
              to={`/providers/${provider.id}`} 
              key={provider.id} 
              className="provider-card"
              data-testid={`provider-card-${provider.id}`}
            >
              <div className="provider-card-header">
                <div className="provider-avatar large">
                  {provider.profile?.avatar_url ? (
                    <img src={provider.profile.avatar_url} alt={provider.business_name} />
                  ) : (
                    <User size={40} />
                  )}
                </div>
                <ProviderBadges provider={provider} />
              </div>
              <div className="provider-card-body">
                <h3>{provider.business_name}</h3>
                {showActivity && (
                  <div className="provider-rating">
                    <Star size={16} fill="currentColor" />
                    <span>{provider.average_rating?.toFixed(1) || 'New'}</span>
                    <span className="review-count">({provider.review_count} reviews)</span>
                  </div>
                )}
                <div className="provider-services">
                  {provider.services?.slice(0, 3).map(svc => (
                    <span key={svc.id} className="service-tag">{svc.name}</span>
                  ))}
                </div>
                <span className="view-all-services" data-testid={`view-all-services-${provider.id}`}>
                  View all services <ArrowRight size={13} />
                </span>
                {showActivity ? (
                  <div className="provider-stats">
                    <span><Clock size={14} /> {provider.years_experience || 0} yrs exp</span>
                    <span><CheckCircle size={14} /> {provider.jobs_completed} jobs</span>
                    {provider.hourly_rate_min && (
                      <span>${provider.hourly_rate_min}/hr</span>
                    )}
                  </div>
                ) : (provider.years_experience > 0 || provider.hourly_rate_min) ? (
                  <div className="provider-stats provider-stats-compact">
                    {provider.years_experience > 0 && (
                      <span><Clock size={14} /> {provider.years_experience} yrs exp</span>
                    )}
                    {provider.hourly_rate_min && (
                      <span className="price">From ${provider.hourly_rate_min}/hr</span>
                    )}
                  </div>
                ) : null}
                {provider.city && (
                  <div className="provider-location">
                    <MapPin size={14} />
                    <span>{provider.city}</span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No providers found. Try adjusting your filters.</p>
        </div>
      )}
    </div>
  );
};


export default ProvidersPage;
